import React, { useEffect, useState } from 'react';
import AmountInput from '../ui/AmountInput';
import { extractApiError } from '../../utils/errorMessages';
import { canonicalizeAmount } from '../../utils/amountFormat';
import {
    enterManualPrice,
    fetchBenchmarks,
    updateManualPrice,
} from '../../services/paperPricingService';
import {
    BATE_LABELS,
    PRICE_POINTS,
    fieldErrorsFrom,
    formatDay,
    formatPrice,
    requireList,
    todayIso,
    unitLabel,
    unplacedFieldErrors,
} from './paperPricingFormat';
import { FormField } from './PaperPricingParts';
import PaperPricingModal from './PaperPricingModal';

const FORM_FIELDS = ['benchmark_id', 'assessed_on', 'bate', 'value'];
const PICKABLE_BATES = PRICE_POINTS.map((point) => point.value);

/**
 * Enter one price by hand, or correct one typed earlier (ledger row 132, AC10).
 *
 * A typed price is stored as Manual. The server refuses to type over a price the
 * Platts file already confirmed ("correct it by uploading the corrected file"),
 * and the next file for the same day replaces a manual price and marks it
 * Confirmed -- both are the server's rules and its words; this window only asks.
 *
 * mode 'add':  benchmark (active only), day, price point, value.
 * mode 'edit': a manual row from the board; only the value can change.
 */
const EnterPriceModal = ({ mode, row, date, bate, onClose, onSaved }) => {
    const editing = mode === 'edit';
    const [benchmarks, setBenchmarks] = useState(null);
    const [listError, setListError] = useState('');
    const [benchmarkId, setBenchmarkId] = useState('');
    const [assessedOn, setAssessedOn] = useState(date || todayIso());
    const [pricePoint, setPricePoint] = useState(PICKABLE_BATES.includes(bate) ? bate : 'c');
    const [value, setValue] = useState(editing ? canonicalizeAmount(row.assessment.value) : '');
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (editing) return undefined;
        let cancelled = false;
        fetchBenchmarks({ active: 'true' })
            .then((data) => {
                const results = requireList(data, 'results');
                if (!cancelled) setBenchmarks(results);
            })
            .catch((err) => {
                if (!cancelled) {
                    setListError(extractApiError(err, 'The benchmark list could not be loaded. Close this window and try again.'));
                }
            });
        return () => {
            cancelled = true;
        };
    }, [editing]);

    const chosen = editing
        ? row.benchmark
        : (benchmarks || []).find((benchmark) => String(benchmark.id) === String(benchmarkId));
    const unit = chosen ? unitLabel(chosen.currency, chosen.unit) : '';

    const pickBenchmark = (id) => {
        setBenchmarkId(id);
        const picked = (benchmarks || []).find((benchmark) => String(benchmark.id) === String(id));
        // Start on the benchmark's own default price point; the person can change it.
        if (picked && PICKABLE_BATES.includes(picked.default_bate)) setPricePoint(picked.default_bate);
    };

    const submit = async (event) => {
        event.preventDefault();
        const problems = {};
        if (!editing && !benchmarkId) problems.benchmark_id = 'Pick the benchmark this price is for.';
        if (!editing && !assessedOn) problems.assessed_on = 'Enter the day this price is for.';
        if (!value) problems.value = 'Enter the price.';
        setErrors(problems);
        setFormError('');
        if (Object.keys(problems).length) return;

        setSaving(true);
        try {
            const pointLabel = BATE_LABELS[pricePoint] || pricePoint;
            if (editing) {
                await updateManualPrice(row.assessment.id, value);
                onSaved({
                    date,
                    bate: pricePoint,
                    message: `Saved the manual ${pointLabel} price for ${row.benchmark.code} on ${formatDay(date)}.`,
                });
            } else {
                await enterManualPrice({ benchmarkId: Number(benchmarkId), assessedOn, bate: pricePoint, value });
                onSaved({
                    date: assessedOn,
                    bate: pricePoint,
                    message: `Saved ${chosen.code} ${pointLabel} for ${formatDay(assessedOn)} as a manual price.`,
                });
            }
        } catch (err) {
            const fields = fieldErrorsFrom(err);
            if (fields) {
                setErrors(fields);
                setFormError(unplacedFieldErrors(fields, FORM_FIELDS) || 'Fix the fields marked below, then save again.');
            } else {
                setFormError(extractApiError(err, 'The price could not be saved. Try again.'));
            }
            setSaving(false);
        }
    };

    const footer = (
        <>
            <button type="button" className="pp-btn pp-btn--secondary" onClick={onClose} disabled={saving}>
                Cancel
            </button>
            <button type="submit" className="pp-btn pp-btn--primary" disabled={saving || (!editing && !benchmarks)}>
                {saving ? 'Saving…' : 'Save price'}
            </button>
        </>
    );

    return (
        <PaperPricingModal
            title={editing ? 'Edit manual price' : 'Enter price'}
            eyebrow="Market Prices"
            onClose={onClose}
            busy={saving}
            onSubmit={submit}
            footer={footer}
        >
            {formError ? <div className="pp-alert pp-alert--error" role="alert">{formError}</div> : null}

            {editing ? (
                <dl className="pp-detail-list pp-detail-list--form">
                    <dt>Benchmark</dt>
                    <dd><span className="pp-code">{row.benchmark.code}</span> {row.benchmark.name}</dd>
                    <dt>Day</dt>
                    <dd>{formatDay(date)}</dd>
                    <dt>Price point</dt>
                    <dd>{BATE_LABELS[pricePoint] || pricePoint}</dd>
                    <dt>Price now</dt>
                    <dd className="pp-num-inline">{formatPrice(row.assessment.value)} {unit}</dd>
                </dl>
            ) : (
                <>
                    {listError ? <div className="pp-alert pp-alert--error" role="alert">{listError}</div> : null}
                    <FormField label="Benchmark" htmlFor="pp-price-benchmark" required error={errors.benchmark_id}>
                        <select
                            id="pp-price-benchmark"
                            className="pp-select"
                            value={benchmarkId}
                            onChange={(event) => pickBenchmark(event.target.value)}
                            disabled={!benchmarks || saving}
                        >
                            <option value="">
                                {benchmarks ? 'Pick a benchmark…' : 'Loading benchmarks…'}
                            </option>
                            {(benchmarks || []).map((benchmark) => (
                                <option key={benchmark.id} value={benchmark.id}>
                                    {benchmark.code} · {benchmark.name} ({unitLabel(benchmark.currency, benchmark.unit)})
                                </option>
                            ))}
                        </select>
                    </FormField>
                    {benchmarks && benchmarks.length === 0 ? (
                        <p className="pp-hint">
                            There are no active benchmarks. Add one on Benchmarks &amp; Instruments first.
                        </p>
                    ) : null}
                    <div className="pp-form-row">
                        <FormField label="Day" htmlFor="pp-price-date" required error={errors.assessed_on}>
                            <input
                                id="pp-price-date"
                                type="date"
                                className="pp-input"
                                value={assessedOn}
                                onChange={(event) => setAssessedOn(event.target.value)}
                                disabled={saving}
                            />
                        </FormField>
                        <FormField label="Price point" htmlFor="pp-price-bate" required error={errors.bate}>
                            <select
                                id="pp-price-bate"
                                className="pp-select"
                                value={pricePoint}
                                onChange={(event) => setPricePoint(event.target.value)}
                                disabled={saving}
                            >
                                {PRICE_POINTS.map((point) => (
                                    <option key={point.value} value={point.value}>{point.label}</option>
                                ))}
                            </select>
                        </FormField>
                    </div>
                </>
            )}

            <FormField
                label={editing ? 'New price' : 'Price'}
                htmlFor="pp-price-value"
                required
                error={errors.value}
                hint={unit ? `In ${unit}.` : undefined}
            >
                <AmountInput
                    id="pp-price-value"
                    className="pp-input pp-input--number"
                    value={value}
                    onChange={setValue}
                    maxDecimals={4}
                    placeholder="e.g. 770.04"
                    disabled={saving}
                />
            </FormField>

            <p className="pp-hint">
                A price typed here is marked Manual. The next price file with the same day replaces it and
                marks it Confirmed.
            </p>
        </PaperPricingModal>
    );
};

export default EnterPriceModal;
